import { useCallback, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useSetRecoilState } from 'recoil';
import { useRoiPreviewSubject } from '~/containers/viewer/viewport/useRoiPreviewSubject';
import {
    useActiveEditTarget,
    useActiveEditTargetSeriesUid,
} from '~/hooks/target/useActiveEditTarget';
import type { OrbitView } from '~/engine/util/geometry/camera';
import { useCrossSectionPreviewVolume } from '~/hooks/viewer/useCrossSectionPreviewVolume';
import { roiPreview3DOpenState } from '~/state/atoms/viewerAtoms';
import { GridWidget } from '~/viewportGrid';
import FittedRoiPreviewPane from './FittedRoiPreviewPane';

/** CSS px. */
const OPENING_SIZE = { widthPx: 260, heightPx: 260 };

/**
 * The 3D structure preview widget, while there is a subject to show. The
 * camera pose survives the pane's remounts on collapse or maximize.
 */
export function useStructurePreviewWidget(): ReadonlyArray<GridWidget> {
    const intl = useIntl();
    const setOpen = useSetRecoilState(roiPreview3DOpenState);
    const close = useCallback(() => setOpen(false), [setOpen]);
    const subject = useRoiPreviewSubject();
    const uncommittedVolume = useCrossSectionPreviewVolume();
    const label = useActiveEditTarget().structureSet?.structureSetLabel;
    const hostSeriesUid = useActiveEditTargetSeriesUid();
    const lastViewRef = useRef<OrbitView | undefined>(undefined);
    const rememberView = useCallback((view: OrbitView) => {
        lastViewRef.current = view;
    }, []);
    return useMemo((): GridWidget[] => {
        if (subject === undefined) {
            return [];
        }
        return [
            {
                id: 'structure-preview',
                title: intl.formatMessage({ id: 'structure.preview3D' }),
                subject: label,
                hostSeriesUid,
                openingSize: OPENING_SIZE,
                onClose: close,
                body: () => (
                    <FittedRoiPreviewPane
                        {...subject}
                        uncommittedVolume={uncommittedVolume}
                        initialView={lastViewRef.current}
                        onViewChange={rememberView}
                    />
                ),
            },
        ];
    }, [
        intl,
        subject,
        uncommittedVolume,
        label,
        hostSeriesUid,
        rememberView,
        close,
    ]);
}
